import type {
  DateGrain,
  StoryFilterBarAppliedFilter,
  StoryFilterChipKind,
  StoryFilterChipPartId,
  StoryFilterColumn,
  StoryFilterColumnType,
  StoryFilterOperator,
} from "../../components/molecules/StoryFilterBar/StoryFilterBar.types";
import type { ChartPointAction } from "../../components/molecules/ChartPointActionMenu/ChartPointActionMenu.types";
import type { MultiSelectOption } from "../../hooks/useMultiSelectSearch";
import type { DataModelFieldCategory } from "../../components/registry/dataModelFieldSections";
import { storyFilterComparators, storyFilterListLimit } from "../../components/registry/storyFilterComparators";
import type { FilterValueShape, StoryFilterComparatorConfig } from "../../components/registry/storyFilterComparators";
import type { CanvasItemFullApi, FieldRefApi } from "./storyCanvas.types";
import type { ClickCondition } from "../../components/molecules/ChartWidget/chartMarkSelect";
import { toCollapsedValueList } from "./crossFilterMappers";
import { isScalarAfter, parseRangeNumber, toPeriodLabel } from "./filterRangeMappers";
import {
  samePeriod,
  DEFAULT_WEEK_START,
  toPeriodRange,
  toPeriodText,
} from "../../components/molecules/StoryFilterValueEditor/storyFilterDatePresets";
import type {
  ItemFilterState,
  NewPathPointState,
  PointActionAvailability,
  StoryFilterBuckets,
  StoryFilterChip,
  StoryFilterSessionView,
  StoryTrailAction,
} from "./storyFilterChips.types";
import { storyFilterChipLockedCopy, storyFilterChipSharingCopy } from "./storyFilterChips.copy";
import type {
  FilterComparator,
  FilterScalarApi,
  RelativePeriodApi,
  StoryFieldApi,
  StoryFilterAlsoRequestApi,
  StoryFilterApi,
  StoryFilterCreateRequestApi,
  StoryFilterListResponseApi,
  StoryFilterOriginApi,
  StoryFilterPatchRequestApi,
  WeekdayApi,
} from "./storyFilters.types";

const TRAIL_KINDS = new Set<StoryFilterApi["kind"]>(["global", "cross", "drill_down"]);
/** A chart's own chips. Its drill step is not one: it shows as the item GET's drill chip. */
const ITEM_CHIP_KINDS = new Set<StoryFilterApi["kind"]>(["local", "baseline"]);

const COMPARATOR_TEXT: Record<FilterComparator, string> = {
  eq: "is",
  neq: "is not",
  in: "is",
  not_in: "is not",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  between: "between",
  contains: "contains",
  is_null: "is",
  relative_date: "is",
};

/** A value as a chip shows it: a date at its field's grain (`Nov 2016`), null as "empty". */
const scalarText = (value: FilterScalarApi, grain: DateGrain | null): string => {
  if (value === null) return "empty";
  return grain && typeof value === "string" ? toPeriodLabel(value, grain) : String(value);
};

/**
 * e.g. `Region is West, East +2 more`, `Order Month between Nov 2016 and Feb 2017`
 * — the label every chip and crumb shows; `title` names every value for the
 * tooltip. The comparator reads as the operator menu names it for the field's
 * type, a date at the grain of its hierarchy level, and a range always shows
 * both bounds.
 */
export const toFilterChipLabel = (filter: StoryFilterApi): { label: string; title: string } => {
  const conditions = toFilterChipConditions(filter);
  return {
    label: conditions.map((c) => `${c.field} ${c.operator} ${c.value}`).join(" · "),
    title: conditions.map((c) => `${c.field} ${c.operator} ${c.fullValue}`).join(" · "),
  };
};

/** One condition of a chip: its three texts; `fullValue` names every value, for the tooltip. */
export interface FilterChipConditionParts {
  field: string;
  operator: string;
  value: string;
  fullValue: string;
}

/**
 * Every condition a filter holds as the three texts of its chip, the first from the row itself
 * and one more for each entry of a cross filter's `also` (a click implying several conditions).
 */
export const toFilterChipConditions = (filter: StoryFilterApi): FilterChipConditionParts[] => [
  toFilterChipParts(filter),
  ...(filter.also ?? []).map((also) =>
    toFilterChipParts({
      ...filter,
      ...also,
      value_type: also.comparator === "eq" || also.comparator === "in" ? "categorical" : "continuous",
      hierarchy: null,
    }),
  ),
];

/** A `relative_date` value: a rolling period in the spec's words. */
export const isRelativePeriod = (value: unknown): value is RelativePeriodApi =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A `relative_date` value in words ("Last 30 days"), the chip's and the tile subtitle's one phrase; "" for no period. */
export const toRelativePeriodText = (value: unknown): string => (isRelativePeriod(value) ? toPeriodText(value) : "");

/**
 * A date range draft's values: `[from, to]`, plus the rolling period as JSON when a preset or "Last N"
 * picked it. Only that pick saves as rolling; Range-tab dates never do, whatever they equal.
 */
export const toRangeDraftValues = (from: string, to: string, period?: RelativePeriodApi | null): string[] =>
  period ? [from, to, JSON.stringify(period)] : [from, to];

/** The rolling period a draft's values carry (`toRangeDraftValues`), else null. */
export const toDraftPeriod = (values: readonly string[]): RelativePeriodApi | null => {
  if (values.length !== 3) return null;
  try {
    const parsed: unknown = JSON.parse(values[2]);
    return isRelativePeriod(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** The date tab a filter reopens on: Relative for a new one or a rolling period, Range for fixed dates. */
export const toReopenDateTab = (values: readonly string[] | undefined): "relative" | "range" =>
  values && !toDraftPeriod(values) && values.some((value) => value.trim() !== "") ? "range" : "relative";

/** A temporal `between` draft picked as a rolling period saves as `relative_date` with it; anything else as is. */
const toRollingCondition = (
  comparator: FilterComparator,
  valueType: StoryFieldApi["value_type"],
  value: FilterScalarApi | FilterScalarApi[],
  values: readonly string[],
): { comparator: FilterComparator; value: FilterScalarApi | FilterScalarApi[] | RelativePeriodApi } => {
  const period = comparator === "between" && valueType === "temporal" ? toDraftPeriod(values) : null;
  return period ? { comparator: "relative_date", value: period } : { comparator, value };
};

/**
 * A filter's first condition as the three texts of its chip (`Region | is | West, East +2 more`).
 * The operator reads as the menu names it for the field's type.
 */
export const toFilterChipParts = (filter: StoryFilterApi): FilterChipConditionParts => {
  const { value, comparator } = filter;
  const field = filter.field_name;
  if (comparator === "is_null") return { field, operator: "is", value: "empty", fullValue: "empty" };
  if (comparator === "relative_date") {
    const text = toRelativePeriodText(value);
    return { field, operator: COMPARATOR_TEXT.relative_date, value: text, fullValue: text };
  }
  const operator =
    storyFilterComparators[filter.value_type].find((entry) => entry.comparator === comparator)?.label ??
    COMPARATOR_TEXT[comparator];
  const grain = toDateGrain(filter);
  const values = (Array.isArray(value) ? value : [value]).map((scalar) => scalarText(scalar, grain));
  if (comparator === "between") {
    const text = values.join(" and ");
    return { field, operator, value: text, fullValue: text };
  }
  return { field, operator, value: toCollapsedValueList(values), fullValue: values.join(", ") };
};

const bySeq = (a: StoryFilterApi, b: StoryFilterApi): number =>
  (a.seq ?? 0) - (b.seq ?? 0);

/**
 * What a trail row was done with, for its icon. The comparator is read first, so a click that was
 * later flipped to Remove reads as Remove. A cross row with no `origin` (written before the API
 * sent it) is Keep only.
 */
export const toTrailAction = (filter: StoryFilterApi): StoryTrailAction => {
  if (filter.kind === "global") return "filter";
  if (filter.kind === "drill_down") return "drill";
  if (filter.comparator === "not_in") return "remove";
  return filter.origin === "click" ? "cross" : "keep";
};

/** A value list as a set of its text, so a scalar and a one-value array compare alike. */
const toValueTexts = (value: StoryFilterApi["value"]): string[] =>
  (Array.isArray(value) ? value : [value]).map(String);

/**
 * The ids of cross rows a field drill folds into its own drill step: a `drill_down` row whose
 * previous trail row is a `cross` row taken from that same chart, whose value holds the drill's.
 */
export const toFoldedCrossIds = (trailRows: readonly StoryFilterApi[]): Set<string> => {
  const folded = new Set<string>();
  trailRows.forEach((row, index) => {
    const previous = trailRows[index - 1];
    if (row.kind !== "drill_down" || previous?.kind !== "cross") return;
    if (!row.canvas_item_id || previous.source_canvas_item_id !== row.canvas_item_id) return;
    const crossValues = toValueTexts(previous.value);
    if (toValueTexts(row.value).some((text) => crossValues.includes(text))) folded.add(previous.id);
  });
  return folded;
};

/** The trail with each field drill's folded cross row dropped, so one action reads as one step. */
export const foldFieldDrills = (trailFilters: readonly StoryFilterApi[]): StoryFilterApi[] => {
  const folded = toFoldedCrossIds(trailFilters);
  return trailFilters.filter((row) => !folded.has(row.id));
};

/**
 * What was done from each chart, by item id, for the badges on its title: a cross row goes to
 * the chart it was clicked in, a drill row to the chart that drilled. Read from the unfolded rows,
 * so a field drill's chart shows both its cross or keep icon and its drill icon. De-duplicated, in
 * trail order. The old path names no source chart, so it has no cross badge.
 */
export const toItemTrailActions = (trailFilters: readonly StoryFilterApi[]): Record<string, StoryTrailAction[]> => {
  const byItemId: Record<string, StoryTrailAction[]> = {};
  const add = (itemId: string | null | undefined, action: StoryTrailAction) => {
    if (!itemId) return;
    const actions = (byItemId[itemId] ??= []);
    if (!actions.includes(action)) actions.push(action);
  };
  trailFilters.forEach((row) => {
    if (row.kind === "cross") add(row.source_canvas_item_id, toTrailAction(row));
    else if (row.kind === "drill_down") add(row.canvas_item_id, "drill");
  });
  return byItemId;
};

/**
 * The trail id to rewind to when a drill step is removed: the step before it, or before the
 * cross row folded into it. Null when nothing precedes it.
 */
export const toDrillRewindTarget = (trailFilters: readonly StoryFilterApi[], drillId: string): string | null => {
  const index = trailFilters.findIndex((row) => row.id === drillId);
  if (index < 0) return null;
  const folded = toFoldedCrossIds(trailFilters);
  const before = index - 1;
  const start = before >= 0 && folded.has(trailFilters[before].id) ? before : index;
  return trailFilters[start - 1]?.id ?? null;
};

export const toStoryFilterBuckets = ({
  can_edit: canEdit,
  filters,
}: StoryFilterListResponseApi): StoryFilterBuckets => {
  const trailRows = filters.filter((f) => TRAIL_KINDS.has(f.kind)).sort(bySeq);
  const latestTrailId = trailRows.length ? trailRows[trailRows.length - 1].id : null;

  const toChip = (filter: StoryFilterApi): StoryFilterChip => {
    const isTrailStep = TRAIL_KINDS.has(filter.kind);
    return {
      id: filter.id,
      ...toFilterChipLabel(filter),
      filter,
      isInteractive: canEdit && (!isTrailStep || filter.id === latestTrailId),
    };
  };

  const chipsByItemId: Record<string, StoryFilterChip[]> = {};
  filters.forEach((filter) => {
    if (!ITEM_CHIP_KINDS.has(filter.kind) || !filter.canvas_item_id) return;
    (chipsByItemId[filter.canvas_item_id] ??= []).push(toChip(filter));
  });

  const foldedCrossIds = toFoldedCrossIds(trailRows);

  return {
    globalChips: trailRows.filter((f) => f.kind === "global").map(toChip),
    crossChips: trailRows.filter((f) => f.kind === "cross" && !foldedCrossIds.has(f.id)).map(toChip),
    drillChips: trailRows.filter((f) => f.kind === "drill_down").map(toChip),
    trail: trailRows.map(toChip),
    latestTrailId,
    chipsByItemId,
    canEdit,
  };
};

const toChipKind = (filter: StoryFilterApi): StoryFilterChipKind => {
  if (filter.kind === "drill_down") return "drillDown";
  if (filter.kind !== "cross") return "global";
  if (filter.comparator === "not_in") return "exclude";
  return filter.origin === "click" ? "cross" : "include";
};

/**
 * Parts of a saved filter's chip the API refuses to change, with why. The PATCH never changes a
 * filter's field; a drill step is never changed at all; an earlier trail step only while it is the latest.
 */
const toLockedParts = (filter: StoryFilterApi, isInteractive: boolean): { parts: StoryFilterChipPartId[]; title: string } => {
  if (!isInteractive) {
    return {
      parts: ["field", "operator", "value"],
      title: filter.kind === "drill_down" ? storyFilterChipLockedCopy.drill : storyFilterChipLockedCopy.notLatest,
    };
  }
  // The latest drill step changes its target and value; a drill is always "is", so its operator stays.
  if (filter.kind === "drill_down") return { parts: ["operator"], title: storyFilterChipLockedCopy.drillOperator };
  return { parts: [], title: "" };
};

/** What the session view may offer beyond the server's list. */
export interface StoryFilterSessionOptions {
  /** The caller owns the Story, so a personal chip offers to keep its filter for everyone. */
  canKeep?: boolean;
}

const toAppliedFilter = (
  { id, label, title, filter, isInteractive }: StoryFilterChip,
  { canKeep = false }: StoryFilterSessionOptions,
): StoryFilterBarAppliedFilter => {
  const conditions = toFilterChipConditions(filter).map(({ field, operator, value }) => ({ field, operator, value }));
  const locked = toLockedParts(filter, isInteractive);
  const personal = filter.personal === true;
  return {
    id,
    label,
    title,
    kind: toChipKind(filter),
    conditions,
    lockedParts: locked.parts,
    lockedTitle: locked.title,
    // Only the latest trail step, and a chart's own or baseline filter, can be removed.
    isRemovable: isInteractive,
    tag: personal ? storyFilterChipSharingCopy.onlyYou : undefined,
    actionLabel: personal && canKeep ? storyFilterChipSharingCopy.keepForEveryone : undefined,
  };
};

const EMPTY_VIEW: StoryFilterSessionView = {
  topBarChips: [],
  crossBarChips: [],
  drillBarChips: [],
  breadcrumbs: [],
  chartChipsByItemId: {},
  serverChipIds: new Set(),
  trailFilters: [],
  latestTrailId: null,
  editableFilters: {},
  hasFilters: false,
  canEdit: false,
};

/**
 * A drill to a picked field reads in the trail as its chart's drill step does
 * ("International → Order Date"), not as the filter row's "Region is International".
 */
export const withDrillStepLabels = <Crumb extends { id?: string; label: string; title?: string }>(
  crumbs: readonly Crumb[],
  stateByItemId: Readonly<Record<string, ItemFilterState>>,
): Crumb[] => {
  const labels: Record<string, string> = {};
  Object.values(stateByItemId).forEach((state) =>
    state.drill?.path.forEach((step) => {
      if (step.label) labels[step.filter_id] = step.label;
    }),
  );
  return crumbs.map((crumb) => {
    const label = crumb.id ? labels[crumb.id] : undefined;
    return label ? { ...crumb, label, title: label } : crumb;
  });
};

/** Null (not loaded, failed, or disabled) renders as no server filters and no controls. */
export const toStoryFilterSessionView = (
  buckets: StoryFilterBuckets | null,
  options: StoryFilterSessionOptions = {},
): StoryFilterSessionView => {
  if (!buckets) return EMPTY_VIEW;
  const toChip = (chip: StoryFilterChip) => toAppliedFilter(chip, options);
  const foldedCrossIds = toFoldedCrossIds(buckets.trail.map((chip) => chip.filter));
  const chartChipsByItemId = Object.fromEntries(
    Object.entries(buckets.chipsByItemId).map(([itemId, chips]) => [itemId, chips.map(toChip)]),
  );
  const serverChipIds = new Set([
    ...buckets.trail.map((chip) => chip.id),
    ...Object.values(buckets.chipsByItemId).flat().map((chip) => chip.id),
  ]);
  return {
    topBarChips: buckets.globalChips.map(toChip),
    crossBarChips: buckets.crossChips.map(toChip),
    drillBarChips: buckets.drillChips.map(toChip),
    breadcrumbs: buckets.trail
      .filter((chip) => !foldedCrossIds.has(chip.id))
      .map(({ id, label, title, filter }) => ({
        id,
        label,
        title,
        action: toTrailAction(filter),
        sourceItemId: filter.source_canvas_item_id ?? null,
      })),
    chartChipsByItemId,
    serverChipIds,
    trailFilters: buckets.trail.map((chip) => chip.filter),
    latestTrailId: buckets.latestTrailId,
    editableFilters: Object.fromEntries(
      [...buckets.trail, ...Object.values(buckets.chipsByItemId).flat()]
        .filter((chip) => chip.isInteractive)
        .map((chip) => [chip.id, chip.filter]),
    ),
    hasFilters: serverChipIds.size > 0,
    canEdit: buckets.canEdit,
  };
};

const BLANK_LABEL = "Blank";

/**
 * One neutral chip per story filter a chart keeps its own condition against, named by the
 * filter's field. An id the story no longer lists is dropped.
 */
export const toKeptChips = (
  keptIds: readonly string[],
  filters: readonly StoryFilterApi[],
  keptLabel: (fieldName: string) => string,
): StoryFilterBarAppliedFilter[] =>
  keptIds.flatMap((id) => {
    const filter = filters.find((candidate) => candidate.id === id);
    return filter ? [{ id: `kept:${id}`, label: keptLabel(filter.field_name), isRemovable: false }] : [];
  });

/**
 * Reads the item GET's filter fields. A legacy item has none of them, so it
 * comes back not filterable with nothing to show. The drill chip is the
 * deepest step; its level is found by `level_no`, since levels can have gaps.
 */
export const toItemFilterState = (item: CanvasItemFullApi | undefined, newPath = false): ItemFilterState => {
  const deepest = item?.drill?.path[item.drill.path.length - 1];
  const level = deepest
    ? item?.drill?.levels.find((candidate) => candidate.level_no === deepest.level_no)
    : undefined;
  const label = deepest
    ? deepest.label || (deepest.value === null ? BLANK_LABEL : String(deepest.value))
    : "";
  return {
    // Absent (item not loaded, or an older backend) is not a legacy item, so it is never greyed.
    filterable: item?.filterable ?? true,
    isNotApplicable: (item?.filters_not_applicable?.length ?? 0) > 0,
    keptIds: item?.filters_kept ?? [],
    overriddenIds: new Set(item?.filters_overridden ?? []),
    drillChip: deepest
      ? {
          id: deepest.filter_id,
          label,
          // A drill to a picked field's label already names both ends.
          title: deepest.label ? deepest.label : level ? `${level.name}: ${label}` : undefined,
          kind: "drillDown",
          // Removing it drills the chart up one level.
          isRemovable: true,
        }
      : null,
    axisField: item?.axis_field ?? null,
    axisColumn: item?.axis_column ?? null,
    ...toAxisPlot(item?.spec, item?.axis_column ?? null),
    drill: item?.drill ?? null,
    ...(newPath ? { newPath: toNewPathPointState(item) } : {}),
  };
};

/** A table's cell, as a new-path drawing writes it. */
interface TableCell {
  row: number;
  column: number;
  text: string;
}

const isTableCell = (value: unknown): value is TableCell =>
  isSpecObject(value) && typeof value.row === "number" && typeof value.column === "number" && typeof value.text === "string";

/** A table writes a missing value as a dash. */
const MISSING_TEXT = "\u2013";

const toCellValue = (text: string): FilterScalarApi => (text === MISSING_TEXT ? null : text);

const toNewPathPointState = (item: CanvasItemFullApi | undefined): NewPathPointState => {
  const tableColumns = item?.table_columns ?? null;
  const tableRows: Record<string, FilterScalarApi>[] = [];
  const values = isSpecObject(item?.spec) && isSpecObject(item?.spec.data) ? item?.spec.data.values : undefined;
  if (tableColumns && Array.isArray(values)) {
    values.filter(isTableCell).forEach(({ row, column, text }) => {
      const name = tableColumns[column];
      if (name === undefined) return;
      tableRows[row] = { ...tableRows[row], [name]: toCellValue(text) };
    });
  }
  return {
    axisGrain: item?.axis_grain ?? null,
    drillColumns: item?.drill_columns ?? [],
    tableColumns,
    tableRows,
    tableFields: item?.table_fields ?? {},
  };
};

type SpecObject = Record<string, unknown>;

const isSpecObject = (value: unknown): value is SpecObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Keys under which a view nests more views: a combo's layers, a concat's views, a facet's or repeat's spec. */
const NESTED_VIEW_KEYS = ["layer", "concat", "hconcat", "vconcat", "spec"];

/**
 * Every channel definition that plots `column`, in any view of the spec: x on a
 * column chart, y on a bar chart, color on a pie, a layer's x on a combo. The
 * tooltip only displays a column, so it never decides how the axis is plotted.
 */
const findAxisChannels = (spec: unknown, column: string): SpecObject[] => {
  if (!isSpecObject(spec)) return [];
  const encoding = isSpecObject(spec.encoding) ? spec.encoding : {};
  const own = Object.entries(encoding).flatMap(([channel, definition]) =>
    channel !== "tooltip" && isSpecObject(definition) && definition.field === column ? [definition] : [],
  );
  const nested = NESTED_VIEW_KEYS.flatMap((key) => {
    const views = spec[key];
    return (Array.isArray(views) ? views : [views]).flatMap((view) => findAxisChannels(view, column));
  });
  return [...own, ...nested];
};

/** `utcyearmonth` and `{unit: "yearmonth", utc: true}` bucket in UTC; `yearmonth` buckets in local time. */
const isUtcTimeUnit = (timeUnit: unknown): boolean =>
  typeof timeUnit === "string"
    ? timeUnit.startsWith("utc")
    : isSpecObject(timeUnit) && (timeUnit.utc === true || String(timeUnit.unit ?? "").startsWith("utc"));

/** How the spec plots the axis column: as a date (and on which clock), and under a renamed key or not. */
const toAxisPlot = (
  spec: unknown,
  column: string | null,
): Pick<ItemFilterState, "axisDateClock" | "axisIsBucketed"> => {
  const channels = column ? findAxisChannels(spec, column) : [];
  const timeUnits = channels.flatMap(({ timeUnit }) => (timeUnit == null ? [] : [timeUnit]));
  // `bin: "binned"` means the rows arrive already binned, so the column keeps its name.
  const isBinned = channels.some(({ bin }) => bin != null && bin !== false && bin !== "binned");
  const isDate = timeUnits.length > 0 || channels.some(({ type }) => type === "temporal");
  return {
    axisDateClock: !isDate ? null : timeUnits.every(isUtcTimeUnit) ? "utc" : "local",
    axisIsBucketed: timeUnits.length > 0 || isBinned,
  };
};

// ─── Data point → Include / Exclude / Drill down ───────────────────────────────

/**
 * Include and Exclude need the chart's axis field; a chart with none (or a
 * legacy one) has all three disabled. Drill down also needs a level below.
 */
export const toPointActionAvailability = (state: ItemFilterState): PointActionAvailability => {
  if (state.newPath) return toNewPathAvailability(state, state.newPath);
  const hasAxis = state.filterable && state.axisField !== null && state.axisColumn !== null;
  return {
    include: hasAxis,
    exclude: hasAxis,
    drillDown: hasAxis && !!state.drill?.can_drill_down,
  };
};

/** The days a date axis's unit spans past its first, by the unit; a day spans none. */
const PERIOD_ENDS: Readonly<Partial<Record<string, (first: Date) => void>>> = {
  day: () => {},
  week: (first) => first.setUTCDate(first.getUTCDate() + 6),
  month: (first) => first.setUTCMonth(first.getUTCMonth() + 1, 0),
  quarter: (first) => first.setUTCMonth(first.getUTCMonth() + 3, 0),
  year: (first) => first.setUTCFullYear(first.getUTCFullYear() + 1, 0, 0),
};

/** A date axis's unit; a date by no unit is by the day. Undefined for one the menu can't span (an hour, a fiscal year). */
const toPeriodEnd = (state: ItemFilterState, point: NewPathPointState) =>
  state.axisDateClock === null ? undefined : PERIOD_ENDS[point.axisGrain ?? "day"];

/**
 * A new-path chart: a table drills the group columns its render says can be
 * drilled, and includes or excludes by a group column's field. A date axis by a
 * period longer than a day includes the period and can't exclude it; by a unit
 * the menu can't span, it does neither.
 */
const toNewPathAvailability = (state: ItemFilterState, point: NewPathPointState): PointActionAvailability => {
  if (point.tableColumns) {
    const filters = state.filterable && Object.keys(point.tableFields).length > 0;
    return { include: filters, exclude: filters, drillDown: state.filterable && point.drillColumns.length > 0 };
  }
  const hasAxis = state.filterable && state.axisField !== null && state.axisColumn !== null;
  const isDate = state.axisDateClock !== null;
  const end = toPeriodEnd(state, point);
  const spans = !isDate || end !== undefined;
  return {
    include: hasAxis && spans,
    exclude: hasAxis && spans && (!isDate || (point.axisGrain ?? "day") === "day"),
    drillDown:
      hasAxis && (!!state.drill?.can_drill_down || !!state.drill?.by_field || (state.axisColumn !== null && point.drillColumns.includes(state.axisColumn))),
  };
};

/** A new-path chart whose Drill down goes straight to the next level, with no field to pick: a table, a date axis. */
export const drillsWithoutPicker = (state: ItemFilterState): boolean =>
  !!state.newPath && (state.newPath.tableColumns !== null || state.axisDateClock !== null);

/** How Vega-Lite starts the name of a time unit or bin, which it puts before the column it renames. */
const BUCKET_KEY_PREFIX = /^(bin|utc|timeunit|year|quarter|month|week|day|date|hours|minutes|seconds|milliseconds)/;

/**
 * The axis value of a clicked row. A time unit or bin plots the column under a
 * new key (`utcyearmonth_Order Month`, `bin_maxbins_10_Price`), and an aggregated
 * row keeps only that key, so it is read before the column itself.
 */
export const findAxisValue = (datum: Record<string, unknown>, column: string, isBucketed: boolean): unknown => {
  if (isBucketed) {
    const suffix = `_${column}`;
    const key = Object.keys(datum).find(
      (candidate) => candidate.endsWith(suffix) && BUCKET_KEY_PREFIX.test(candidate.slice(0, -suffix.length)),
    );
    if (key !== undefined) return datum[key];
  }
  return datum[column];
};

/**
 * A clicked bin's `[start, end)`: Vega-Lite names the start `bin_maxbins_N_<column>` and the end the same
 * plus `_end`. Undefined for a row that is not a bin of that column.
 */
export const findBinRange = (datum: Record<string, unknown>, column: string): [number, number] | undefined => {
  const startKey = Object.keys(datum).find((key) => key.startsWith("bin_") && key.endsWith(`_${column}`));
  const start = startKey ? datum[startKey] : undefined;
  const end = startKey ? datum[`${startKey}_end`] : undefined;
  return typeof start === "number" && typeof end === "number" ? [start, end] : undefined;
};

const padDatePart = (part: number): string => String(part).padStart(2, "0");

/** A date's `YYYY-MM-DD` on the clock its axis buckets in; undefined for an invalid date. */
const toDayString = (date: Date, clock: "utc" | "local"): string | undefined => {
  if (Number.isNaN(date.getTime())) return undefined;
  if (clock === "utc") return date.toISOString().slice(0, 10);
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

/**
 * A clicked value as the API takes it: scalars as they are, a date as its
 * `YYYY-MM-DD` day, anything else not at all. Vega hands a date over as a
 * `Date`; epoch ms counts as a date only on a date axis.
 */
const toPointValue = (raw: unknown, dateClock: ItemFilterState["axisDateClock"]): FilterScalarApi | undefined => {
  if (raw instanceof Date) return toDayString(raw, dateClock ?? "utc");
  if (raw === null || typeof raw === "string" || typeof raw === "boolean") return raw as FilterScalarApi;
  if (typeof raw !== "number") return undefined;
  return dateClock ? toDayString(new Date(raw), dateClock) : raw;
};

/**
 * The create body for a point-menu action on a clicked datum. The field is the
 * item's `axis_field` (never a spec channel); the value is the datum's
 * `axis_column`, under the key the spec's time unit or bin gave it. Include and
 * Exclude are a `cross` `in` / `not_in`; Drill down is a `drill_down` with only
 * the clicked member, since the server picks the level by default — unless
 * `drillField` is given (the user picked a field from the drill-down field
 * picker's Hierarchy tab), in which case it's sent so the server drills that
 * field's hierarchy instead. Null when the chart can't take the action or the
 * datum has no usable value, so no request is made. A new-path cross request carries `origin`
 * (a plain click, Keep only or Remove) when given; the old path sends none.
 */
export const toPointFilterRequest = (
  state: ItemFilterState,
  datum: Record<string, unknown>,
  action: "include" | "exclude" | "drillDown",
  canvasItemId?: string,
  drillField?: FieldRefApi | null,
  origin?: StoryFilterOriginApi,
  conditions?: ClickCondition[],
): StoryFilterCreateRequestApi | null => {
  if (!toPointActionAvailability(state)[action]) return null;
  if (state.newPath) {
    return toNewPathRequest(state, state.newPath, datum, action, canvasItemId, drillField, origin, conditions);
  }
  if (!state.axisField || !state.axisColumn) return null;
  const value = toPointValue(findAxisValue(datum, state.axisColumn, state.axisIsBucketed), state.axisDateClock);
  if (value === undefined) return null;
  if (action === "drillDown") {
    return canvasItemId
      ? { kind: "drill_down", canvas_item_id: canvasItemId, value, ...(drillField ? { field: drillField } : {}) }
      : null;
  }
  const bin = state.axisDateClock === null && action === "include" ? findBinRange(datum, state.axisColumn) : undefined;
  if (bin) return { kind: "cross", field: state.axisField, comparator: "between", value: bin };
  return {
    kind: "cross",
    field: state.axisField,
    comparator: action === "include" ? "in" : "not_in",
    value: [value],
  };
};

/** A further condition as the API takes it: a bin range is its start included and its end not. */
const toAlsoRequest = (condition: ClickCondition): StoryFilterAlsoRequestApi[] =>
  condition.op === "between"
    ? [
        { field_name: condition.field, op: "gte", value: condition.value[0] },
        { field_name: condition.field, op: "lt", value: condition.value[1] },
      ]
    : [{ field_name: condition.field, op: "eq", value: condition.value }];

/** The clicked row's values by column, for a drill to fix with the click; the server keeps the group columns'. */
const toClickedRow = (datum: Record<string, unknown>): Record<string, FilterScalarApi> =>
  Object.fromEntries(
    Object.entries(datum).flatMap(([column, raw]) => {
      const value = toPointValue(raw, null);
      return value === undefined ? [] : [[column, value]];
    }),
  );

const toDay = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * Keep only or Remove from a table's cell: by the clicked column's field when it groups the rows,
 * else by the row's first group column that has one (a click on a figure means its row).
 */
const toTableCellFilter = (
  point: NewPathPointState,
  cell: TableCell,
  action: "include" | "exclude",
  canvasItemId?: string,
  origin?: StoryFilterOriginApi,
): StoryFilterCreateRequestApi | null => {
  const columns = point.tableColumns ?? [];
  const clicked = columns[cell.column];
  const column = clicked !== undefined && point.tableFields[clicked] ? clicked : columns.find((name) => point.tableFields[name]);
  if (column === undefined) return null;
  const value = point.tableRows[cell.row]?.[column];
  if (value === undefined || value === null) return null;
  return {
    kind: "cross",
    field: point.tableFields[column],
    comparator: action === "include" ? "in" : "not_in",
    value: [value],
    ...(canvasItemId ? { canvas_item_id: canvasItemId } : {}),
    ...(origin ? { origin } : {}),
  };
};

/**
 * A new-path Story's point-menu request. A drill names the clicked column and
 * row: a table's cell by its column's name and the row's other cells. Include
 * of a period longer than a day is the period's `between`, first day to last.
 */
const toNewPathRequest = (
  state: ItemFilterState,
  point: NewPathPointState,
  datum: Record<string, unknown>,
  action: "include" | "exclude" | "drillDown",
  canvasItemId?: string,
  drillField?: FieldRefApi | null,
  origin?: StoryFilterOriginApi,
  conditions?: ClickCondition[],
): StoryFilterCreateRequestApi | null => {
  if (point.tableColumns) {
    if (!isTableCell(datum)) return null;
    if (action !== "drillDown") return toTableCellFilter(point, datum, action, canvasItemId, origin);
    if (!canvasItemId) return null;
    const column = point.tableColumns[datum.column];
    if (column === undefined || !point.drillColumns.includes(column)) return null;
    const row = { ...point.tableRows[datum.row] };
    return { kind: "drill_down", canvas_item_id: canvasItemId, value: toCellValue(datum.text), column, row };
  }
  if (!state.axisField || !state.axisColumn) return null;
  const value = toPointValue(findAxisValue(datum, state.axisColumn, state.axisIsBucketed), state.axisDateClock);
  if (value === undefined) return null;
  if (action === "drillDown") {
    if (!canvasItemId) return null;
    const row = { ...toClickedRow(datum), [state.axisColumn]: value };
    return {
      kind: "drill_down",
      canvas_item_id: canvasItemId,
      value,
      column: state.axisColumn,
      row,
      ...(drillField ? { field: drillField } : {}),
    };
  }
  // Names the clicked chart so the server filters every other chart but not this one.
  // Include sends every other condition the click implies on the same filter (the server
  // needs the chart to find their fields).
  const axisColumn = state.axisColumn;
  const others =
    action === "include" && canvasItemId
      ? (conditions ?? []).filter((c) => c.field !== axisColumn).flatMap(toAlsoRequest)
      : [];
  const source = {
    ...(canvasItemId ? { canvas_item_id: canvasItemId } : {}),
    ...(origin ? { origin } : {}),
    ...(others.length ? { also: others } : {}),
  };
  const end = toPeriodEnd(state, point);
  if (action === "include" && end && typeof value === "string" && (point.axisGrain ?? "day") !== "day") {
    const last = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(last.getTime())) return null;
    end(last);
    return { kind: "cross", field: state.axisField, comparator: "between", value: [value, toDay(last)], ...source };
  }
  const bin = state.axisDateClock === null && action === "include" ? findBinRange(datum, state.axisColumn) : undefined;
  if (bin && canvasItemId) {
    // A bin is [start, end): `between` includes its end, so the end is a further `lt`.
    const upTo = { field_name: axisColumn, op: "lt" as const, value: bin[1] };
    return {
      kind: "cross",
      field: state.axisField,
      comparator: "gte",
      value: bin[0],
      ...source,
      also: [upTo, ...others],
    };
  }
  if (bin) return { kind: "cross", field: state.axisField, comparator: "between", value: bin, ...source };
  return {
    kind: "cross",
    field: state.axisField,
    comparator: action === "include" ? "in" : "not_in",
    value: [value],
    ...source,
  };
};

// ─── /fields → the Add filter picker ───────────────────────────────────────────

/**
 * Picker key for a field. A role-playing field ("Year (ship_date)") shares its
 * `model_field_id` with its siblings, so the role joins the key.
 */
export const toFieldKey = ({ model_field_id: id, role }: FieldRefApi): string =>
  role ? `${id}:${role}` : id;

const COLUMN_TYPE_BY_CATEGORY: Record<DataModelFieldCategory, StoryFilterColumnType> = {
  dates: "date",
  dimensions: "text",
  geographic: "geo",
  measures: "number",
  calculated: "measure",
};

/** The Calendar hierarchy's levels, top down. */
const DATE_GRAIN_BY_LEVEL: Partial<Record<number, DateGrain>> = { 1: "year", 2: "quarter", 3: "month", 4: "day" };

/**
 * A date field's grain, from its `level_no` in the Calendar hierarchy. A date
 * with no hierarchy, or on a level outside it, gets the day picker: the server
 * buckets any day it receives to the field's own grain. Null for a non-date.
 */
export const toDateGrain = ({
  value_type,
  hierarchy,
}: Pick<StoryFieldApi, "value_type" | "hierarchy">): DateGrain | null => {
  if (value_type !== "temporal") return null;
  return (hierarchy ? DATE_GRAIN_BY_LEVEL[hierarchy.level_no] : undefined) ?? "day";
};

/** Categorical and date fields have an editor; the number range comes later (plan slice 15). */
export const toStoryFilterColumns = (fields: StoryFieldApi[]): StoryFilterColumn[] =>
  fields.map((field) => ({
    id: toFieldKey(field.field),
    label: field.name,
    type: COLUMN_TYPE_BY_CATEGORY[field.category],
    grain: toDateGrain(field) ?? undefined,
    disabled: field.value_type === "continuous",
    hierarchy: field.hierarchy,
  }));

/**
 * The operators a field's filter offers, in menu order: the backend's
 * comparator table for its value type, less what the menu hides, and less
 * `in` / `not_in` on a measure (the backend takes those on numeric dimensions only).
 */
export const toComparatorOptions = (field: StoryFieldApi): StoryFilterComparatorConfig[] =>
  storyFilterComparators[field.value_type].filter(
    (entry) => !entry.opensAs && !(entry.dimensionOnly && field.field.kind === "measure"),
  );

/** An Include/Exclude row is an `in` / `not_in` of one value; the backend rejects any other comparator on it. */
const CROSS_COMPARATORS: ReadonlySet<FilterComparator> = new Set<FilterComparator>(["in", "not_in"]);

/**
 * A field's operator menu: each comparator it takes, valued by the comparator
 * itself. Editing an Include/Exclude row (`kind` "cross") offers only its two.
 */
export const toOperatorOptions = (field: StoryFieldApi, kind?: StoryFilterApi["kind"]): StoryFilterOperator[] =>
  toComparatorOptions(field)
    .filter((entry) => kind !== "cross" || CROSS_COMPARATORS.has(entry.comparator))
    .map(({ comparator, label }) => ({ id: comparator, label }));

/**
 * The operator a new filter on this field starts with, so the user never picks one first:
 * "in" (reads "is") for a category, "between" for a date or a number. Falls back to the
 * menu's first operator when the field does not take it.
 */
export const toDefaultOperatorId = (field: StoryFieldApi): string | undefined => {
  const preferred: FilterComparator = field.value_type === "categorical" ? "in" : "between";
  const options = toOperatorOptions(field);
  return options.find((option) => option.id === preferred)?.id ?? options[0]?.id;
};

/** The words a chip shows for a draft's operator id (the local mock chips carry "is" / "is not"). */
export const toOperatorText = (operatorId: string | null): string => {
  const comparator = toDraftComparator(operatorId);
  return COMPARATOR_TEXT[comparator] ?? comparator;
};

/** Operator-pill ids for "relative" and "last N" on a day-grain date: both open the Relative tab on a `between` range. */
export const RELATIVE_OPERATOR_ID = "ui:relative";
export const LAST_N_OPERATOR_ID = "ui:last_n";
export const isRelativeOperatorId = (id: string | null): boolean => id === RELATIVE_OPERATOR_ID || id === LAST_N_OPERATOR_ID;

/**
 * Operators the design shows that the filter contract does not take. "relative" and "last N" are live
 * for a day-grain date: they work out to a fixed `between` range (PENDING-FILTER-OPERATORS). "starts with"
 * and "top N" stay disabled teasers: the query engine has no such comparator.
 */
const PENDING_OPERATORS: Record<StoryFieldApi["value_type"], StoryFilterOperator[]> = {
  categorical: [{ id: "pending:starts_with", label: "starts with", soon: true }],
  continuous: [{ id: "pending:top_n", label: "top N", soon: true }],
  temporal: [],
};

const RELATIVE_OPERATORS: StoryFilterOperator[] = [
  { id: RELATIVE_OPERATOR_ID, label: "relative" },
  { id: LAST_N_OPERATOR_ID, label: "last N" },
];

/** A field's operator menu followed by the not-yet-built ones. Not for an Include/Exclude row. */
export const toOperatorOptionsWithPending = (field: StoryFieldApi, kind?: StoryFilterApi["kind"]): StoryFilterOperator[] => [
  ...toOperatorOptions(field, kind),
  ...(kind === "cross" ? [] : PENDING_OPERATORS[field.value_type]),
  ...(kind !== "cross" && toDateGrain(field) === "day" ? RELATIVE_OPERATORS : []),
];

/** The old menu's "is" / "is not" ids, which the local mock chips still carry; every other id is a comparator. */
const LEGACY_OPERATOR_COMPARATORS: Readonly<Record<string, FilterComparator>> = { is: "in", is_not: "not_in" };

/** A draft's operator as the comparator it sends; `in` when none is picked. */
export const toDraftComparator = (operatorId: string | null): FilterComparator =>
  (operatorId === null ? "in" : LEGACY_OPERATOR_COMPARATORS[operatorId] ?? operatorId) as FilterComparator;

/**
 * The menu row a draft's operator shows as: the row with its id, else the row
 * for the comparator an old "is" / "is not" id stands for, else the first row.
 */
export const toSelectedOperator = (
  operators: StoryFilterOperator[],
  operatorId: string | null,
): StoryFilterOperator | undefined =>
  operators.find((operator) => operator.id === operatorId) ??
  operators.find((operator) => operator.id === toDraftComparator(operatorId)) ??
  operators[0];

/** One draft value as the API takes it: a number for a continuous field, the text otherwise; undefined when blank or not a number. */
const toDraftScalar = (text: string, valueType: StoryFieldApi["value_type"]): FilterScalarApi | undefined => {
  if (valueType === "continuous") return parseRangeNumber(text) ?? undefined;
  return text.trim() === "" ? undefined : text;
};

/**
 * A draft's values in the shape its comparator sends (the registry's
 * `valueShape`): the one value, a `[low, high]` pair in order, the list, or
 * `null` for `is_null`. Undefined while the draft can't be sent: the field's
 * type doesn't take the comparator, a value is blank, the count is wrong for
 * the shape, or a list is past the backend's limit.
 */
export const toFilterValue = (
  comparator: FilterComparator,
  valueType: StoryFieldApi["value_type"],
  values: string[],
): FilterScalarApi | FilterScalarApi[] | undefined => {
  const entry = storyFilterComparators[valueType].find((candidate) => candidate.comparator === comparator);
  if (!entry) return undefined;
  if (entry.valueShape === "none") return null;
  const own = entry.valueShape === "pair" && toDraftPeriod(values) ? values.slice(0, 2) : values;
  const scalars = own.map((text) => toDraftScalar(text, valueType));
  if (scalars.some((scalar) => scalar === undefined)) return undefined;
  const picked = scalars as FilterScalarApi[];
  if (entry.valueShape === "one") return picked.length === 1 ? picked[0] : undefined;
  if (entry.valueShape === "pair") {
    if (picked.length !== 2) return undefined;
    const [low, high] = picked;
    return isScalarAfter(low, high) ? [high, low] : [low, high];
  }
  return picked.length >= 1 && picked.length <= storyFilterListLimit ? picked : undefined;
};

/** What a draft's comparator asks the editor for; a chip with no API field behind it keeps the checklist. */
export const toValueShape = (field: StoryFieldApi | undefined, operatorId: string | null): FilterValueShape => {
  if (!field) return "list";
  const comparator = toDraftComparator(operatorId);
  return storyFilterComparators[field.value_type].find((entry) => entry.comparator === comparator)?.valueShape ?? "list";
};

/** The value area a draft shows, by its comparator's shape × the field's type and date grain. */
export type StoryFilterValueEditorKind =
  | "checklist"
  | "text"
  | "date"
  | "period"
  | "dateRange"
  | "periodRange"
  | "numberRange"
  | "none";

/**
 * The editor for a draft: the value checklist for a list (`is`, `is any of`),
 * one picker for one value (`on`, `before`, `contains`), From/To only for
 * `between`, nothing for `is empty`. A date is picked on the calendar at a day
 * grain and from the list of periods at a year, quarter or month grain.
 */
export const toValueEditorKind = (
  field: StoryFieldApi | undefined,
  operatorId: string | null,
): StoryFilterValueEditorKind => {
  const shape = toValueShape(field, operatorId);
  if (!field || shape === "list") return "checklist";
  if (shape === "none") return "none";
  const grain = toDateGrain(field);
  if (shape === "one") return grain === null ? "text" : grain === "day" ? "date" : "period";
  if (grain !== null) return grain === "day" ? "dateRange" : "periodRange";
  return field.value_type === "continuous" ? "numberRange" : "checklist";
};

/**
 * A draft's values fitted to a new comparator's shape, so switching the
 * operator keeps what still fits: the first value for one, the first two (blank
 * when missing) for a pair, the non-blank ones for a list, none for `is empty`.
 */
export const toShapeValues = (values: string[], shape: FilterValueShape): string[] => {
  if (shape === "none") return [];
  if (shape === "one") return values.slice(0, 1);
  if (shape === "pair") return [values[0] ?? "", values[1] ?? ""];
  return values.filter((value) => value.trim() !== "");
};

/** The picker hands back a key; this finds the field behind it (ref for a POST, `active_filter_id` for the editor). */
export const toStoryFieldsByKey = (fields: StoryFieldApi[]): Record<string, StoryFieldApi> =>
  Object.fromEntries(fields.map((field) => [toFieldKey(field.field), field]));

/**
 * A page of raw values as menu rows. The option id is the raw value (`West`),
 * so a selection can be sent back as-is. A null value has no row yet: how a
 * blank is picked and sent is unconfirmed with the backend.
 */
export const toFieldValueOptions = (
  values: FilterScalarApi[],
  grain: DateGrain | null = null,
  measures: (number | null)[] | null = null,
): MultiSelectOption[] =>
  values.flatMap((value, index) =>
    value === null
      ? []
      : [
          {
            value: String(value),
            label: scalarText(value, grain),
            ...(measures ? { measure: measures[index] ?? null } : {}),
          },
        ],
  );

// ─── Add filter → POST /filters ────────────────────────────────────────────────

/**
 * A picked field, an operator and the draft's values as a create body:
 * `global` for the top bar, or `local` when a chart's `canvasItemId` is given.
 * The whole field ref goes up, role included, and the value takes the shape
 * the comparator sends. Null while the draft can't be sent (see
 * `toFilterValue`), so an empty or half-picked draft never reaches the server.
 */
export const toFilterCreateRequest = (
  field: StoryFieldApi,
  operatorId: string | null,
  values: string[],
  canvasItemId?: string | null,
): StoryFilterCreateRequestApi | null => {
  const comparator = toDraftComparator(operatorId);
  const value = toFilterValue(comparator, field.value_type, values);
  if (value === undefined) return null;
  const base = { field: field.field, ...toRollingCondition(comparator, field.value_type, value, values) };
  return canvasItemId
    ? { kind: "local", canvas_item_id: canvasItemId, ...base }
    : { kind: "global", ...base };
};

/**
 * The point menu's actions with the item GET's verdict applied: Include,
 * Exclude and Drill down grey out when the chart can't take them (no axis
 * field, no hierarchy, or already at the last level).
 */
export const withPointAvailability = (
  actions: ChartPointAction[],
  availability: PointActionAvailability,
): ChartPointAction[] =>
  actions.map((action) =>
    action.id === "include" || action.id === "exclude" || action.id === "drillDown"
      ? { ...action, disabled: action.disabled || !availability[action.id] }
      : action,
  );

// ─── Edit a saved chip → PATCH /filters/{id} ───────────────────────────────────

/** The editor's draft, in the shape the filter bar state holds. */
export interface FilterEditDraft {
  id: string;
  columnId: string;
  operatorId: string | null;
  values: string[];
}

/** Keep an open draft's chip key/anchor when POST replaces it with a server row. */
export const mergeFilterDraftChips = (
  savedChips: StoryFilterBarAppliedFilter[],
  draftChips: StoryFilterBarAppliedFilter[],
  serverIdsByDraftId: Readonly<Record<string, string>>,
): StoryFilterBarAppliedFilter[] => {
  const draftIdsByServerId = Object.fromEntries(
    Object.entries(serverIdsByDraftId).map(([draftId, serverId]) => [serverId, draftId]),
  );
  const savedIds = new Set(savedChips.map(({ id }) => id));
  return [
    ...savedChips.map((chip) => ({ ...chip, id: draftIdsByServerId[chip.id] ?? chip.id })),
    ...draftChips.filter(({ id }) => !savedIds.has(serverIdsByDraftId[id] ?? id)),
  ];
};

const toValueStrings = (value: StoryFilterApi["value"]): string[] =>
  (Array.isArray(value) ? value : [value]).filter((v) => v !== null).map(String);

/**
 * The comparator a saved filter opens with in the menu: its own, or the menu
 * comparator a hidden one stands for (a text `eq` opens as `in`). Null when its
 * value type doesn't take it, e.g. `relative_date`: that chip stays read-only.
 */
const toMenuComparator = (filter: StoryFilterApi): FilterComparator | null => {
  const entry = storyFilterComparators[filter.value_type].find((candidate) => candidate.comparator === filter.comparator);
  if (!entry) return null;
  return entry.opensAs ?? entry.comparator;
};

/** A saved filter as the editor opens it: its menu comparator as the operator, its values as text. */
export const toFilterEditDraft = (
  filter: StoryFilterApi,
  now: Date = new Date(),
  weekStart: WeekdayApi = DEFAULT_WEEK_START,
): FilterEditDraft | null => {
  if (filter.comparator === "relative_date") {
    // A rolling period reopens as its dates today, still carrying the period, so it saves as rolling again.
    const period = isRelativePeriod(filter.value) ? filter.value : null;
    const range = period ? toPeriodRange(period, now, weekStart) : null;
    return period && range
      ? {
          id: filter.id,
          columnId: toFieldKey(filter.field),
          operatorId: "between",
          values: toRangeDraftValues(range.from, range.to, period),
        }
      : null;
  }
  const comparator = toMenuComparator(filter);
  return comparator
    ? { id: filter.id, columnId: toFieldKey(filter.field), operatorId: comparator, values: toValueStrings(filter.value) }
    : null;
};

/**
 * A saved row's new field as a PATCH body: `drill_to` for a drill step, `field` for any other row;
 * null when the pick is the field it already has.
 */
export const toFieldChangeRequest = (
  original: StoryFilterApi,
  field: StoryFieldApi,
): StoryFilterPatchRequestApi | null => {
  if (toFieldKey(original.field) === toFieldKey(field.field)) return null;
  return original.kind === "drill_down" ? { drill_to: field.field } : { field: field.field };
};

/**
 * What the editor changed, as a PATCH body in the shape its comparator sends;
 * null when nothing changed, or when the draft can't be sent (clearing every
 * value is a removal, not an edit). A text `eq` that opened as `in` and was
 * left alone counts as unchanged.
 */
export const toFilterPatchRequest = (
  original: StoryFilterApi,
  draft: { operatorId: string | null; values: string[] },
  now: Date = new Date(),
  weekStart: WeekdayApi = DEFAULT_WEEK_START,
): StoryFilterPatchRequestApi | null => {
  const comparator = toDraftComparator(draft.operatorId);
  const value = toFilterValue(comparator, original.value_type, draft.values);
  if (value === undefined) return null;
  const period = comparator === "between" && original.value_type === "temporal" ? toDraftPeriod(draft.values) : null;
  if (period) {
    const saved: unknown = original.comparator === "relative_date" ? original.value : null;
    return isRelativePeriod(saved) && samePeriod(saved, period) ? null : { comparator: "relative_date", value: period };
  }
  const before = toFilterEditDraft(original, now, weekStart);
  const after = toValueStrings(value);
  const unchanged =
    before?.operatorId === comparator &&
    before.values.length === after.length &&
    before.values.every((text, index) => text === after[index]);
  return unchanged ? null : { comparator, value };
};

/**
 * The chart a write is changing, so its card can show it is updating while the
 * request is in flight; null for a story-wide write, a rewind, or a filter
 * that is not in `savedFilters`. A story-wide write reaches its charts through
 * the refetch that follows it instead.
 */
export const toWriteTargetItemId = (
  write: { type: "create"; body: StoryFilterCreateRequestApi } | { type: "patch" | "remove"; filterId: string } | { type: "rewind" },
  savedFilters: Record<string, StoryFilterApi>,
): string | null => {
  if (write.type === "rewind") return null;
  if (write.type === "create") return "canvas_item_id" in write.body ? write.body.canvas_item_id : null;
  return savedFilters[write.filterId]?.canvas_item_id ?? null;
};
